import { defineMessages } from '../../i18n/messages';

export const PrivacyMessages = defineMessages({
  PAGE_TITLE: {
    id: 'privacy.page_title',
    text: 'Privacy Policy',
    description: 'Page title for the privacy policy page',
  },
  SUBTITLE: {
    id: 'privacy.subtitle',
    text: 'How we handle your data at Localingos.',
    description: 'Subtitle shown below the page title on the privacy policy page',
  },
  WHAT_WE_COLLECT_TITLE: {
    id: 'privacy.what_we_collect_title',
    text: 'What We Collect',
    description: 'Section header on the privacy policy page for data collection details',
  },
  COLLECT_ITEM_1: {
    id: 'privacy.collect_item_1',
    text: 'Account information (email, name) provided at signup via Google OAuth or email',
    description: 'List item under What We Collect section. Google OAuth is a third-party login method',
  },
  COLLECT_ITEM_2: {
    id: 'privacy.collect_item_2',
    text: 'Content you submit for localization (source strings, translations)',
    description: 'List item under What We Collect section. Source strings are the original text submitted for translation',
  },
  COLLECT_ITEM_3: {
    id: 'privacy.collect_item_3',
    text: 'Usage data (word counts, API calls, feature usage)',
    description: 'List item under What We Collect section. API calls are programmatic requests to the service',
  },
  COLLECT_ITEM_4: {
    id: 'privacy.collect_item_4',
    text: 'Payment information processed securely through Stripe. We never store card details',
    description: 'List item under What We Collect section. Stripe is a third-party payment processor',
  },
  HOW_WE_USE_TITLE: {
    id: 'privacy.how_we_use_title',
    text: 'How We Use It',
    description: 'Section header on the privacy policy page for data usage details',
  },
  USE_ITEM_1: {
    id: 'privacy.use_item_1',
    text: 'To provide and improve the Localingos localization platform',
    description: 'List item under How We Use It section',
  },
  USE_ITEM_2: {
    id: 'privacy.use_item_2',
    text: 'To process translations using AI (Amazon Bedrock)',
    description: 'List item under How We Use It section. Amazon Bedrock is an AWS AI service used for translation',
  },
  USE_ITEM_3: {
    id: 'privacy.use_item_3',
    text: 'To manage your subscription and billing',
    description: 'List item under How We Use It section',
  },
  USE_ITEM_4: {
    id: 'privacy.use_item_4',
    text: 'To send transactional emails (trial notifications, billing receipts)',
    description: 'List item under How We Use It section',
  },
  HOW_WE_PROTECT_TITLE: {
    id: 'privacy.how_we_protect_title',
    text: 'How We Protect It',
    description: 'Section header on the privacy policy page for data protection details',
  },
  PROTECT_ITEM_1: {
    id: 'privacy.protect_item_1',
    text: 'All data is encrypted in transit (TLS) and at rest (AES-256)',
    description: 'List item under How We Protect It section. TLS and AES-256 are encryption standards',
  },
  PROTECT_ITEM_2: {
    id: 'privacy.protect_item_2',
    text: 'Infrastructure hosted on AWS with SOC 2 compliant services',
    description: 'List item under How We Protect It section. SOC 2 is a security compliance standard',
  },
  PROTECT_ITEM_3: {
    id: 'privacy.protect_item_3',
    text: 'Access controls enforced via AWS Cognito authentication',
    description: 'List item under How We Protect It section. AWS Cognito is an identity/authentication service',
  },
  PROTECT_ITEM_4: {
    id: 'privacy.protect_item_4',
    text: 'API keys are hashed and never stored in plaintext',
    description: 'List item under How We Protect It section. Hashing is a one-way security transformation',
  },
  THIRD_PARTIES_TITLE: {
    id: 'privacy.third_parties_title',
    text: 'Third Parties',
    description: 'Section header on the privacy policy page for third-party service disclosures',
  },
  THIRD_PARTY_BEDROCK: {
    id: 'privacy.third_party_bedrock',
    text: '<strong>Amazon Bedrock</strong>: AI translation processing. Your content is sent for translation and not used to train models.',
    description: 'Third-party disclosure item. Contains HTML bold tag. Amazon Bedrock is an AWS AI service',
  },
  THIRD_PARTY_STRIPE: {
    id: 'privacy.third_party_stripe',
    text: '<strong>Stripe</strong>: Payment processing. Subject to <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe\'s Privacy Policy</a>.',
    description: 'Third-party disclosure item. Contains HTML bold tag and external link to Stripe privacy policy',
  },
  THIRD_PARTY_AWS: {
    id: 'privacy.third_party_aws',
    text: '<strong>AWS</strong>: Infrastructure hosting (DynamoDB, Lambda, CloudFront, SES, Cognito).',
    description: 'Third-party disclosure item. Contains HTML bold tag. Lists AWS services used for hosting',
  },
  YOUR_RIGHTS_TITLE: {
    id: 'privacy.your_rights_title',
    text: 'Your Rights',
    description: 'Section header on the privacy policy page for user data rights',
  },
  YOUR_RIGHTS_DESC: {
    id: 'privacy.your_rights_desc',
    text: 'You can request export or deletion of your data at any time by contacting us. When you delete your account, all associated content and translations are permanently removed.',
    description: 'Body text under Your Rights section explaining data export and deletion',
  },
  CONTACT_TITLE: {
    id: 'privacy.contact_title',
    text: 'Contact',
    description: 'Section header on the privacy policy page for contact information',
  },
  CONTACT_DESC: {
    id: 'privacy.contact_desc',
    text: 'Questions about privacy? Email us at <a href="mailto:legal@localingos.com">legal@localingos.com</a>.',
    description: 'Body text under Contact section. Contains HTML mailto link',
  },
  SHOPIFY_TITLE: {
    id: 'privacy.shopify_title',
    text: 'The Shopify App',
    description: 'Section header on the privacy policy page covering the Shopify app specifically',
  },
  SHOPIFY_DESC: {
    id: 'privacy.shopify_desc',
    text: 'If you install Localingos from the Shopify App Store, the app reads only the storefront content you select for translation and writes translations back to your shop. What it accesses is listed below.',
    description: 'Body text introducing the Shopify section of the privacy policy',
  },
  SHOPIFY_ACCESS_1: {
    id: 'privacy.shopify_access_1',
    text: 'Translatable storefront content you have selected — product, collection and page text, theme strings, and the languages your shop has enabled.',
    description: 'List item describing what the Shopify app reads from a shop',
  },
  SHOPIFY_ACCESS_2: {
    id: 'privacy.shopify_access_2',
    text: 'Your shop domain, its numeric Shopify identifier, and an offline access token, encrypted at rest and used only to read and write the content above.',
    description: 'List item describing shop identifiers and the access token the app stores',
  },
  SHOPIFY_NO_CUSTOMER_DATA: {
    id: 'privacy.shopify_no_customer_data',
    text: '<strong>No customer data.</strong> Localingos does not request, receive or store any information about your customers — no names, addresses, orders or payment details. The two customer-data webhooks Shopify requires are implemented and have nothing to return or delete.',
    description: 'List item stating the Shopify app holds no shopper data. Contains HTML bold tag',
  },
  SHOPIFY_DELETION: {
    id: 'privacy.shopify_deletion',
    text: 'When you uninstall the app, your access token is discarded immediately. Shopify then asks us to erase your shop\'s data, and we delete your translations, your content records and your shop record. Billing records are kept, because we are required to retain them for accounting — they contain no storefront content.',
    description: 'List item explaining what is deleted when the Shopify app is uninstalled',
  },
  LAST_UPDATED: {
    id: 'privacy.last_updated',
    text: 'Last updated: September 2026',
    description: 'Timestamp shown at the bottom of the privacy policy page',
  },
  BREADCRUMB: {
    id: 'privacy.breadcrumb',
    text: 'Privacy',
    description:
      'Name of this page in the schema.org BreadcrumbList structured data, following the "Home" crumb. Keep it short — it is a navigation trail label, not the page heading "Privacy Policy".',
  },
});
